import PolicyApi from "../../services/PolicyAPI";
import { UpdatePolicyNumberOfDocument } from "../../../DocuMate/services/DocuMateAPI";
import Logger from "../../../shared/services/Logger";
import { PolicyDtoRead } from "../../model/PolicyDtoRead";
import LookupService from "../../../Services/LookupService";
import { CommonUtility } from "../../../Services/CommonUtility";
import { PolicyDto } from "../../model/PolicyDto";
import * as constant from '../../../shared/util/constant';
import { CustomerBasicVehicleValidation } from '../../../shared/util/constant';
import validator from 'validator';
import CommonDataService from "../../../Services/CommonDataService";
import store, { RootState } from "../../../shared/store";
import { useSelector } from 'react-redux';

class PolicyDetailService {
    polciAPI: PolicyApi = new PolicyApi();
    policiesList = useSelector((state: RootState) => state.customerSlice.policies);
    private readonly CURRENT_YEAR = new Date().getFullYear();

    private isLeasedYes(value: unknown): boolean {
        return value === true || value === 1 || value === "1" || value === "true";
    }
        
    private policyDetailsAPIToUI(apiData: PolicyDtoRead): PolicyDtoRead {
        return {
            ...apiData,
            oldPolicyNumber: apiData.policyNumber
        };
    }

    private policyDetailsUIToAPI(uiData: PolicyDto): PolicyDto {
        return {
            ...uiData,
            polEffDate: uiData.polEffDate ? CommonUtility.datetimeToDateOnly(uiData.polEffDate) : undefined,
            polExpDate: uiData.polExpDate ? CommonUtility.datetimeToDateOnly(uiData.polExpDate) : undefined,
            inceptionDate: uiData.inceptionDate ? CommonUtility.datetimeToDateOnly(uiData.inceptionDate) : undefined,
            policyEvents: uiData.policyEvents?.map(event => ({
                ...event,
                tranEffDate: event.tranEffDate ? CommonUtility.datetimeToDateOnly(event.tranEffDate) : undefined
            })),
            policyLineOfBusinesses: (uiData as any).policyLineOfBusinesses?.map((lob: any) => ({
                ...lob,
                policyAutoVehicles: lob.policyAutoVehicles?.map((v: any) => ({
                    ...v,
                    isLeased: (() => { const raw = v.isLeased ?? v.IsLeased; if (raw === null || raw === undefined) return null; return this.isLeasedYes(raw) ? true : false; })(),
                    leasedDate: v.isLeased ?? v.IsLeased
                        ? (v.leasedDate ? CommonUtility.datetimeToDateOnly(v.leasedDate) : v.leasedDate)
                        : null,
                })),
            }))
        };
    }

    getPolicyDetails = async (policyId: string, polEventId: string): Promise<PolicyDtoRead | null> => {
        if (!policyId) return null;
        try {
            const policyDetailsAPIResponse: PolicyDtoRead = await this.polciAPI.fetchPolicyDetailsById(policyId, polEventId);
            // Use mapping method for future-proofing
            return this.policyDetailsAPIToUI(policyDetailsAPIResponse);
        } catch (error) {
            Logger.logError(error.toString());
            throw error;
        }
    };

    updatePolicyDetails = async (policyDetails: PolicyDto): Promise<PolicyDto> => {
        try {
            const { oldPolicyNumber } = policyDetails;
            const custNumber = store.getState().customerSlice.customerSummary?.custNumber;
            const polEffDate = policyDetails.polEffDate ? CommonUtility.formateDate(policyDetails.polEffDate, 'MM/DD/YYYY') : undefined;
            const polExpDate = policyDetails.polExpDate ? CommonUtility.formateDate(policyDetails.polExpDate, 'MM/DD/YYYY') : undefined;
            const apiData = this.policyDetailsUIToAPI(policyDetails);
            const savedDetails = await this.polciAPI.updatePolicyDetails(apiData);
            const newPolicyNumber = savedDetails?.policyNumber;
            if (oldPolicyNumber && newPolicyNumber && oldPolicyNumber !== newPolicyNumber) {
                try {
                    await UpdatePolicyNumberOfDocument({
                        oldPolicyNumber,
                        newPolicyNumber,
                        custNumber: custNumber?.toString(),
                        polEffDate,
                        polExpDate
                    });
                } catch (error) {
                    Logger.logError("Document mapping failed after policy number change", error);
                }
            }
            return {
                ...savedDetails,
                oldPolicyNumber: savedDetails?.policyNumber
            };
        } catch (error) {
            Logger.logError(error.toString());
            throw error;
        }
    };

    public fetchDropDownData = async () => {
        const [
            policyTermStatusList,
            policyTypeList,
            policyBusinessTypeListList,
            policyPaymentPlanList,
            attachTypeList,
            stateUSList,
            lobsList,
            occupancyTypeList,
            residenceTypeList,
            constructionTypeList,
            roofMaterialTypeList,
            heatTypeList,
            sprinklerTypeList,
            dwellingConditionTypeList,
            stormShutterTypeList,
            dwellingLocTypeList,
            secondaryHeatTypeList,
            foundationTypeList,
            transactionTypeList,
            companyList,
            sourceList,
            cancellationReasonList,
            lobApplicationList,
            formTypeList,
            basementTypeList,
            numberOfFloorsTypeList,
            contentLocationTypeList,
            ratingTypeList,
            nearestShoreTypeList,
            elevatedFoundationTypeList,
            elevatedMaterialTypeList,
            enclosedForOtherPurposeTypeList,
            floodOccupancyTypeList,
            protectionDeviceTypeList,
            interestTypeList

        ] = await Promise.all([
            LookupService.fetchPolicyTermStatus(),
            LookupService.fetchPolicyType(),
            LookupService.fetchPolicBusinessType(),
            LookupService.fetchPolicyPaymentPlan(),
            LookupService.fetchAttachType(),
            LookupService.fetchUSStates(),
            LookupService.fetchLineofBusiness(),
            LookupService.fetchOccupancyType(),
            LookupService.fetchResidenceType(),
            LookupService.fetchConstructionType(),
            LookupService.fetchRoofMaterialType(),
            LookupService.fetchHeatType(),
            LookupService.fetchSprinklerType(),
            LookupService.fetchDwellingConditionType(),
            LookupService.fetchStormShutterType(),
            LookupService.fetchDwellingLocType(),
            LookupService.fetchSecondaryHeatType(),
            LookupService.fetchFoundationType(),
            LookupService.fetchTransactionType(),
            CommonDataService.getCompanyList(),
            LookupService.fetchSourceType(),
            LookupService.fetchCancellationReason(),
            LookupService.fetchLOBApplications(),
            LookupService.fetchFormTypes(),
            LookupService.fetchBasementType(),
            LookupService.fetchNumberOfFloorsType(),
            LookupService.fetchContentLocationType(),
            LookupService.fetchRatingType(),
            LookupService.fetchNearestShoreType(),
            LookupService.fetchElevatedFoundationType(),
            LookupService.fetchElevatedMaterialType(),
            LookupService.fetchEnclosedForOtherPurposeType(),
            LookupService.fetchFloodOccupancyType(),
            LookupService.fetchProtectionDeviceType(),
            LookupService.fetchInterestType()
        ]);

        return {
            policyTermStatusLookup: CommonUtility.mapToSelectOptions(policyTermStatusList),
            policyTypeLookup: CommonUtility.mapToSelectOptions(policyTypeList),
            policyBusinessTypeLookup: CommonUtility.mapToSelectOptions(policyBusinessTypeListList),
            policyPaymentPlanLookup: CommonUtility.mapToSelectOptions(policyPaymentPlanList),
            attachTypeLookup: CommonUtility.mapToSelectOptions(attachTypeList),
            stateUSLookup: stateUSList ? stateUSList.map(item => {
                return {
                    label: item.lookupValue,
                    value: item.lookupCode,
                    isInactive: false
                }
            }) : [],
            lobsLookup: CommonUtility.mapToSelectOptions(lobsList),
            occupancyTypeLookup: CommonUtility.mapToSelectOptions(occupancyTypeList),
            residenceTypeLookup: CommonUtility.mapToSelectOptions(residenceTypeList),
            constructionTypeLookup: CommonUtility.mapToSelectOptions(constructionTypeList),
            roofMaterialTypeLookup: CommonUtility.mapToSelectOptions(roofMaterialTypeList),
            heatTypeLookup: CommonUtility.mapToSelectOptions(heatTypeList),
            sprinklerTypeLookup: CommonUtility.mapToSelectOptions(sprinklerTypeList),
            dwellingConditionTypeLookup: CommonUtility.mapToSelectOptions(dwellingConditionTypeList),
            stormShutterTypeLookup: CommonUtility.mapToSelectOptions(stormShutterTypeList),
            dwellingLocTypeLookup: CommonUtility.mapToSelectOptions(dwellingLocTypeList),
            secondaryHeatTypeLookup: CommonUtility.mapToSelectOptions(secondaryHeatTypeList),
            foundationTypeLookup: CommonUtility.mapToSelectOptions(foundationTypeList),
            transactionTypeLookup: transactionTypeList ? transactionTypeList.map(item => {
            return {
                label: item.lookupValue,
                value: item.lookupCode || item.id,
                isInactive: false
            }}) : [],
            companyListLookup: companyList ? companyList.map(item => ({
                label: item.description,
                value: item.id,
                isInactive: item.isActive === false,
                parentId: item.parentCompanyId
            })) : [],
            parentCompanyLookup: companyList ? companyList.filter(item => item.flag === true).map(item => ({
                label: item.description,
                value: item.id,
                isInactive: item.isActive === false,
                parentId: item.parentCompanyId
            })) : [],
            writingCompanyLookup: companyList ? companyList.filter(item => item.flag === false).map(item => ({
                label: item.description,
                value: item.id,
                isInactive: item.isActive === false,
                parentId: item.parentCompanyId
            })) : [],
            sourceListLookup: CommonUtility.mapToSelectOptions(sourceList),
            cancellationReasonLookup: CommonUtility.mapToSelectOptions(cancellationReasonList),
            lobApplicationLookup: CommonUtility.mapToSelectOptions(lobApplicationList),
            formTypeLookup: CommonUtility.mapToSelectOptions(formTypeList),
            basementTypeLookup: CommonUtility.mapToSelectOptions(basementTypeList),
            numberOfFloorsTypeLookup: CommonUtility.mapToSelectOptions(numberOfFloorsTypeList),
            contentLocationTypeLookup: CommonUtility.mapToSelectOptions(contentLocationTypeList),
            ratingTypeLookup: CommonUtility.mapToSelectOptions(ratingTypeList),
            nearestShoreTypeLookup: CommonUtility.mapToSelectOptions(nearestShoreTypeList),
            elevatedFoundationTypeLookup: CommonUtility.mapToSelectOptions(elevatedFoundationTypeList),
            elevatedMaterialTypeLookup: CommonUtility.mapToSelectOptions(elevatedMaterialTypeList),
            enclosedForOtherPurposeTypeLookup: CommonUtility.mapToSelectOptions(enclosedForOtherPurposeTypeList),
            floodOccupancyTypeLookup: CommonUtility.mapToSelectOptions(floodOccupancyTypeList),
            protectionDeviceTypeLookup: CommonUtility.mapToSelectOptions(protectionDeviceTypeList),
            interestTypeLookup: CommonUtility.mapToSelectOptions(interestTypeList)
        };
    }

    public fetchCoverageCodeDropdowns = async (isCoverage: boolean): Promise<any> => {
        try {
            const coverageCodeDropdowns = await this.polciAPI.fetchCoverageCodeDropdowns(isCoverage);
            return coverageCodeDropdowns;
        } catch (error) {
            Logger.logError(error.toString());
            throw error;
        }
    }

    // Flood Underwriting Validation Methods
    private validateReplaceCost(value: any): { isValid: boolean, warningText?: string } {
        if (value === null || value === undefined) return { isValid: true };
        const replaceCost = value.toString().trim();
        if (replaceCost === '') return { isValid: true };
        if (!/^\d{1,10}(\.\d{1,2})?$/.test(replaceCost) || parseFloat(replaceCost) < 0 || parseFloat(replaceCost) > 9999999999.99) {
            return { isValid: false, warningText: constant.FloodUnderwritingValidation.REPLACE_COST_RANGE };
        }
        return { isValid: true };
    }

    private validateCondoNoOfUnits(value: any): { isValid: boolean, warningText?: string } {
        if (value === null || value === undefined) return { isValid: true };
        const condoUnits = value.toString().trim();
        if (condoUnits === '') return { isValid: true };
        if (!/^\d{1,4}$/.test(condoUnits) || parseInt(condoUnits) < 1 || parseInt(condoUnits) > 9999) {
            return { isValid: false, warningText: constant.FloodUnderwritingValidation.CONDO_NO_OF_UNITS_RANGE };
        }
        return { isValid: true };
    }

    private validateBldgDiagramNum(value: any): { isValid: boolean, warningText?: string } {
        if (value === null || value === undefined) return { isValid: true };
        const bldgDiagram = value.toString().trim();
        if (bldgDiagram === '') return { isValid: true };
        if (!/^[a-zA-Z0-9]{1,5}$/.test(bldgDiagram)) {
            return { isValid: false, warningText: constant.FloodUnderwritingValidation.BLDG_DIAGRAM_NUM_RANGE };
        }
        return { isValid: true };
    }

    private validateDiagramNumber(value: any): { isValid: boolean, warningText?: string } {
        if (value === null || value === undefined) return { isValid: true };
        const diagramNum = value.toString().trim();
        if (diagramNum === '') return { isValid: true };
        if (!/^[a-zA-Z0-9]{1,12}$/.test(diagramNum)) {
            return { isValid: false, warningText: constant.FloodUnderwritingValidation.DIAGRAM_NUMBER_RANGE };
        }
        return { isValid: true };
    }

    private validateLowestAdjGrade(value: any): { isValid: boolean, warningText?: string } {
        if (value === null || value === undefined) return { isValid: true };
        const lowestAdj = value.toString().trim();
        if (lowestAdj === '') return { isValid: true };
        if (!/^-?\d{1,3}(\.\d)?$/.test(lowestAdj) || parseFloat(lowestAdj) < -999.9 || parseFloat(lowestAdj) > 999.9) {
            return { isValid: false, warningText: constant.FloodUnderwritingValidation.LOWEST_ADJ_GRADE_RANGE };
        }
        return { isValid: true };
    }

    private validateLowestFloorElev(value: any): { isValid: boolean, warningText?: string } {
        if (value === null || value === undefined) return { isValid: true };
        const lowestFloor = value.toString().trim();
        if (lowestFloor === '') return { isValid: true };
        if (!/^-?\d{1,3}(\.\d)?$/.test(lowestFloor) || parseFloat(lowestFloor) < -999.9 || parseFloat(lowestFloor) > 999.9) {
            return { isValid: false, warningText: constant.FloodUnderwritingValidation.LOWEST_FLOOR_ELEV_RANGE };
        }
        return { isValid: true };
    }

    private validateBaseFloorElev(value: any): { isValid: boolean, warningText?: string } {
        if (value === null || value === undefined) return { isValid: true };
        const baseFloor = value.toString().trim();
        if (baseFloor === '') return { isValid: true };
        if (!/^-?\d{1,3}(\.\d)?$/.test(baseFloor) || parseFloat(baseFloor) < -999.9 || parseFloat(baseFloor) > 999.9) {
            return { isValid: false, warningText: constant.FloodUnderwritingValidation.BASE_FLOOR_ELEV_RANGE };
        }
        return { isValid: true };
    }

    private validateEnclosedFloorType(value: any): { isValid: boolean, warningText?: string } {
        if (value === null || value === undefined) return { isValid: true };
        const enclosedFloor = value.toString().trim();
        if (enclosedFloor === '') return { isValid: true };
        if (!/^[0-9]$/.test(enclosedFloor)) {
            return { isValid: false, warningText: constant.FloodUnderwritingValidation.ENCLOSED_FLOOR_TYPE_RANGE };
        }
        return { isValid: true };
    }

    private validateEnclosedSize(value: any): { isValid: boolean, warningText?: string } {
        if (value === null || value === undefined) return { isValid: true };
        const enclosedSize = value.toString().trim();
        if (enclosedSize === '') return { isValid: true };
        if (!/^\d{1,8}(\.\d)?$/.test(enclosedSize) || parseFloat(enclosedSize) < 0 || parseFloat(enclosedSize) > 99999999) {
            return { isValid: false, warningText: constant.FloodUnderwritingValidation.ENCLOSED_SIZE_RANGE };
        }
        return { isValid: true };
    }

    private validateVin(value: string | undefined) {
        if (!value || value.trim().length === 0) {
            return constant.FIELD_BLANK;
        }
        if (value.length !== 17) {
            return CustomerBasicVehicleValidation.VIN_LENGTH_ERROR;
        }
        return null;
    }

    private validateRegisterState(value: string | undefined) {
        if (!value) {
            return constant.FIELD_BLANK;
        }
        return null;
    }

    private validateVehicleYear(value: string | number | undefined) {
        if (value === undefined || value === null || value === "") {
            return constant.FIELD_BLANK;
        }
        const num = typeof value === "string" ? parseInt(value, 10) : value;
        if (isNaN(num)) {
            return CustomerBasicVehicleValidation.VEHICLE_YEAR_NUMERIC_ERROR;
        }
        if (num < 1900 || num > this.CURRENT_YEAR + 1) {
            return CustomerBasicVehicleValidation.VEHICLE_YEAR_RANGE_ERROR(this.CURRENT_YEAR);
        }
        return null;
    }

    private validateMake(value: string | undefined) {
        if (!value) {
            return constant.FIELD_BLANK;
        }
        return null;
    }

    private validateModel(value: string | undefined) {
        if (!value) {
            return constant.FIELD_BLANK;
        }
        return null;
    }

    private validateBodyType(_value: string | undefined) {
        return null; // Not mandatory
    }

    private validatePurchaseDate(value: string | undefined) {
        if (!value) return null; // Not mandatory
        const trimmed = value.trim();
        const parts = trimmed.split("/");

        // Format must be MM/YY (exactly 2 parts).
        if (parts.length !== 2) {
            return CustomerBasicVehicleValidation.PURCHASE_DATE_FORMAT_ERROR;
        }

        const month = parseInt(parts[0], 10);
        const year = parseInt(parts[1], 10);

        if (isNaN(month) || isNaN(year)) {
            return CustomerBasicVehicleValidation.PURCHASE_DATE_NUMERIC_ERROR;
        }

        // Month must be 01-12.
        if (month < 1 || month > 12) {
            return CustomerBasicVehicleValidation.PURCHASE_DATE_MONTH_ERROR;
        }

        // Two-digit year required.
        if (parts[1].length !== 2) {
            return CustomerBasicVehicleValidation.PURCHASE_DATE_FORMAT_ERROR;
        }

        // Cannot be a future month or year.
        const now = new Date();
        const currentYr = now.getFullYear() % 100;
        const currentMonth = now.getMonth() + 1;
        if (year > currentYr || (year === currentYr && month > currentMonth)) {
            return CustomerBasicVehicleValidation.PURCHASE_DATE_FUTURE_ERROR;
        }

        return null;
    }

    private validateLeasedDate(value: string | undefined) {
        if (!value) return null; // Not mandatory
        const parts = value.split("/");
        if (parts.length !== 3) {
            return CustomerBasicVehicleValidation.LEASED_DATE_FORMAT_ERROR;
        }
        const month = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        if (isNaN(month) || isNaN(day) || isNaN(year)) {
            return CustomerBasicVehicleValidation.LEASED_DATE_NUMERIC_ERROR;
        }
        if (month < 1 || month > 12) {
            return CustomerBasicVehicleValidation.PURCHASE_DATE_MONTH_ERROR;
        }
        if (day < 1 || day > 31) {
            return CustomerBasicVehicleValidation.LEASED_DATE_DAY_ERROR;
        }
        if (parts[2].length !== 4 || year < 1900) {
            return CustomerBasicVehicleValidation.LEASED_DATE_YEAR_ERROR;
        }
        const inputDate = new Date(year, month - 1, day);
        if (inputDate > new Date()) {
            return CustomerBasicVehicleValidation.LEASED_DATE_FUTURE_ERROR;
        }
        return null;
    }

    private validateCostNew(value: string | number | undefined) {
        if (!value && value !== 0) return null; // Not mandatory
        const num = typeof value === "string" ? parseInt(value, 10) : value;
        if (isNaN(num)) {
            return CustomerBasicVehicleValidation.COST_NEW_NUMERIC_ERROR;
        }
        if (num < 0) {
            return CustomerBasicVehicleValidation.COST_NEW_NEGATIVE_ERROR;
        }
        return null;
    }

    public validatePolicyDetails = (policyDetails: PolicyDtoRead): any => {
        let validationErrors = {};
        let InsuredErrorCheckForHighlight = false;
        let CoInsuredErrorCheckForHighlight = false;
        let BasicInfoErrorCheckForHighlight = false;

      
        if (policyDetails.policyInsureds.length > 0) {
            if ((!policyDetails.policyInsureds[0].firstName || policyDetails.policyInsureds[0].firstName.trim().length == 0)
                && (!policyDetails.policyInsureds[0].lastName || policyDetails.policyInsureds[0].lastName.trim().length == 0)
                // && (!policyDetails.policyInsureds[0].decNameApp || policyDetails.policyInsureds[0].decNameApp.trim().length == 0)
            ) {
                validationErrors['firstName'] = { isValid: false, warningText: constant.CUSTOMER_MANDATORY_DEPEDENT_FIELDS_ERROR };
                validationErrors['lastName'] = { isValid: false, warningText: constant.CUSTOMER_MANDATORY_DEPEDENT_FIELDS_ERROR };
                // validationErrors['declarationName'] = {isValid : false, warningText :  constant.CUSTOMER_MANDATORY_FIELDS_ERROR};
                InsuredErrorCheckForHighlight = true;
            }

            if (policyDetails.policyInsureds[0].firstName?.trim().length > 0 && policyDetails.policyInsureds[0].firstName?.trim().length < 2) {
                validationErrors['firstName'] = { isValid: false, warningText: constant.CUSTOMER_FIRST_NAME_MIN_LENGTH };
                InsuredErrorCheckForHighlight = true;
            }

            if (policyDetails.policyInsureds[0].lastName?.trim().length > 0 && policyDetails.policyInsureds[0].lastName?.trim().length < 2) {
                validationErrors['lastName'] = { isValid: false, warningText: constant.CUSTOMER_LAST_NAME_MIN_LENGTH };
                InsuredErrorCheckForHighlight = true;
            }

            // if (!policyDetails.policyInsureds[0].homePhone || policyDetails.policyInsureds[0].homePhone.trim().length == 0)
            // {
            //     validationErrors['homePhone'] = {isValid : false, warningText :  constant.FIELD_CANNOT_BE_BLANK};
            //      InsuredErrorCheckForHighlight=true;
            // }

            if (policyDetails.policyInsureds[0].homePhone && policyDetails.policyInsureds[0].homePhone?.trim().length < 10) {
                validationErrors['homePhone'] = { isValid: false, warningText: constant.CONTACT_NO_RANGE };
                InsuredErrorCheckForHighlight = true;
            }

            if (policyDetails.policyInsureds.length > 0 && !policyDetails.policyInsureds[0].mailIsForeign) {
                let policyInsuredsZipCoden = CommonUtility.validateZipCode(policyDetails.policyInsureds[0].resZip);
                if (!policyInsuredsZipCoden.isValid) {
                    validationErrors['resZip'] = { isValid: false, warningText: policyInsuredsZipCoden.warningText };
                    InsuredErrorCheckForHighlight = true;
                }
            }

            if (policyDetails.policyInsureds.length > 0 && !policyDetails.policyInsureds[0].mailIsForeign) {
                let policyInsuredsMailZipCode = CommonUtility.validateZipCode(policyDetails.policyInsureds[0].mailZip);
                if (!policyInsuredsMailZipCode.isValid) {
                    validationErrors['mailZip'] = { isValid: false, warningText: policyInsuredsMailZipCode.warningText };
                    InsuredErrorCheckForHighlight = true;
                }
            }

            if (policyDetails.policyPreviousAddress.length > 0 && !policyDetails.policyInsureds[0].mailIsForeign) {
                let policyPreviousAddressZip = CommonUtility.validateZipCode(policyDetails.policyPreviousAddress[0].zip);
                if (!policyPreviousAddressZip.isValid) {
                    validationErrors['zip'] = { isValid: false, warningText: policyPreviousAddressZip.warningText };
                    InsuredErrorCheckForHighlight = true;
                }
            }

            if (policyDetails.policyInsureds[0].mobile && policyDetails.policyInsureds[0].mobile?.trim().length < 10) {
                validationErrors['mobile'] = { isValid: false, warningText: constant.CONTACT_NO_RANGE };
                InsuredErrorCheckForHighlight = true;
            }

            if (policyDetails.policyInsureds[0].workPhone && policyDetails.policyInsureds[0].workPhone?.trim().length < 10) {
                validationErrors['workPhone'] = { isValid: false, warningText: constant.CONTACT_NO_RANGE };
                InsuredErrorCheckForHighlight = true;
            }

            if (policyDetails.policyInsureds[0].otherPhone1 && policyDetails.policyInsureds[0].otherPhone1?.trim().length < 10) {
                validationErrors['otherPhone1'] = { isValid: false, warningText: constant.CONTACT_NO_RANGE };
                InsuredErrorCheckForHighlight = true;
            }

            if (policyDetails.policyInsureds[0].otherPhone2 && policyDetails.policyInsureds[0].otherPhone2?.trim().length < 10) {
                validationErrors['otherPhone2'] = { isValid: false, warningText: constant.CONTACT_NO_RANGE };
                InsuredErrorCheckForHighlight = true;
            }

            // const phones = [
            //     { key: 'homePhone', value: policyDetails.policyInsureds[0].homePhone?.trim() },
            //     { key: 'mobile', value: policyDetails.policyInsureds[0].mobile?.trim() },
            //     { key: 'workPhone', value: policyDetails.policyInsureds[0].workPhone?.trim() },
            //     { key: 'otherPhone1', value: policyDetails.policyInsureds[0].otherPhone1?.trim() },
            //     { key: 'otherPhone2', value: policyDetails.policyInsureds[0].otherPhone2?.trim() }
            // ].filter(p => p.value); // remove empty/null

            // CommonUtility.validateDuplicates(phones, validationErrors, constant.DUPLICATE_VALUE);

            // if (!policyDetails.policyInsureds[0].eMail || policyDetails.policyInsureds[0].eMail.trim().length == 0)
            // {
            //     validationErrors['eMail'] = {isValid : false, warningText :  constant.FIELD_CANNOT_BE_BLANK};
            //      InsuredErrorCheckForHighlight=true;
            // }

            if (policyDetails.policyInsureds[0].eMail?.trim().length > 0 && !validator.isEmail(policyDetails.policyInsureds[0].eMail?.trim())) {
                validationErrors['eMail'] = { isValid: false, warningText: constant.CUSTOMER_INVALID_EMAIL };
                InsuredErrorCheckForHighlight = true;
            }

            if (policyDetails.policyInsureds[0].email2?.trim().length > 0 && !validator.isEmail(policyDetails.policyInsureds[0].email2?.trim())) {
                validationErrors['email2'] = { isValid: false, warningText: constant.CUSTOMER_INVALID_EMAIL };
                InsuredErrorCheckForHighlight = true;
            }

            if (policyDetails.policyInsureds[0].email3?.trim().length > 0 && !validator.isEmail(policyDetails.policyInsureds[0].email3?.trim())) {
                validationErrors['email3'] = { isValid: false, warningText: constant.CUSTOMER_INVALID_EMAIL };
                InsuredErrorCheckForHighlight = true;
            }

            const emails = [
                { key: 'eMail', value: policyDetails.policyInsureds[0].eMail?.trim() },
                { key: 'email2', value: policyDetails.policyInsureds[0].email2?.trim() },
                { key: 'email3', value: policyDetails.policyInsureds[0].email3?.trim() }
            ].filter(p => p.value); // remove empty/null

            CommonUtility.validateDuplicates(emails, validationErrors, constant.DUPLICATE_VALUE);

            // if ((!policyDetails.policyInsureds[0].resCounty || policyDetails.policyInsureds[0].resCounty?.trim().length == 0)) {
            //     validationErrors['resCounty'] = {isValid : false, warningText :  constant.FIELD_CANNOT_BE_BLANK};
            //      InsuredErrorCheckForHighlight=true;
            // }
        }
        else {
            InsuredErrorCheckForHighlight = true;
            validationErrors['firstName'] = { isValid: false, warningText: constant.CUSTOMER_MANDATORY_DEPEDENT_FIELDS_ERROR };
            validationErrors['lastName'] = { isValid: false, warningText: constant.CUSTOMER_MANDATORY_DEPEDENT_FIELDS_ERROR };
            // validationErrors['declarationName'] = {isValid : false, warningText :  constant.CUSTOMER_MANDATORY_FIELDS_ERROR};
            // validationErrors['homePhone'] = {isValid : false, warningText :  constant.FIELD_CANNOT_BE_BLANK};
            // validationErrors['mobile'] = {isValid : false, warningText :  constant.FIELD_CANNOT_BE_BLANK};                       
            // validationErrors['eMail'] = {isValid : false, warningText :  constant.FIELD_CANNOT_BE_BLANK};
            // validationErrors['resCounty'] = {isValid : false, warningText :  constant.FIELD_CANNOT_BE_BLANK};
        }

       

        if ((!policyDetails.policyNumber || policyDetails.policyNumber?.trim().length == 0)) {
            validationErrors['policyNumber'] = { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK };
            BasicInfoErrorCheckForHighlight = true;
        }

        if ((!policyDetails.policyTypeId || policyDetails.policyTypeId?.trim().length == 0)) {
            validationErrors['policyTypeId'] = { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK };
            BasicInfoErrorCheckForHighlight = true;
        }

        if (!policyDetails.polExpDate) {
            validationErrors['polExpDate'] = { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK };
            BasicInfoErrorCheckForHighlight = true;
        }

        if ((!policyDetails.typeOfBusId || policyDetails.typeOfBusId?.trim().length == 0)) {
            validationErrors['typeOfBusId'] = { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK };
            BasicInfoErrorCheckForHighlight = true;
        }

        if (!policyDetails?.sourceTypeId) {
            validationErrors['sourceType'] = { isValid: false, warningText: constant.FIELD_BLANK };
            BasicInfoErrorCheckForHighlight = true;
        }
        if (policyDetails?.sourceTypeId && !policyDetails?.sourceId) {
            validationErrors['sourceId'] = { isValid: false, warningText: constant.FIELD_BLANK };
            BasicInfoErrorCheckForHighlight = true;
        }
        if (!policyDetails?.companyId) {
            validationErrors['companyId'] = { isValid: false, warningText: constant.FIELD_BLANK };
            BasicInfoErrorCheckForHighlight = true;
        }
        if (!policyDetails?.writingCompanyId) {
            validationErrors['writingCompanyId'] = { isValid: false, warningText: constant.FIELD_BLANK };
            BasicInfoErrorCheckForHighlight = true;
        }
        
           // Inception Date Validation
        if (policyDetails.policyEvents && policyDetails.policyEvents.length > 0) {
            // Find the minimum (earliest) tranEffDate from policyEvents
            const filteredlist = policyDetails.id
                ? this.policiesList.filter(policy => policy.id === policyDetails.id)
                : [];
            
            // Parse as local midnight to avoid UTC timezone shift
            const minTranEffDateFromList = filteredlist.length > 0 
                ? new Date(Math.min(...filteredlist.map(p => new Date(String(p.tranEffDate).substring(0, 10) + 'T00:00:00').getTime())))
                : null;
               
                const inceptionDate = new Date(policyDetails.inceptionDate as Date);
                var inceptionDatetoCompare = CommonUtility.datetimeToDateOnlyObject(inceptionDate)
                var minDateToCompare = CommonUtility.datetimeToDateOnlyObject(minTranEffDateFromList)
                if (minDateToCompare && inceptionDatetoCompare && inceptionDatetoCompare > minDateToCompare) {
                    const formattedMinDate = minDateToCompare.toLocaleDateString('en-US');
                    validationErrors['inceptionDate'] = { isValid: false, warningText: `Inception date must be on or before the earliest in-force transaction effective date (${formattedMinDate}).` };
                    BasicInfoErrorCheckForHighlight = true;
                }
        }

        // Validate transaction effective date if policyEvents exist
        if (policyDetails.policyEvents && policyDetails.policyEvents.length > 0) {
            policyDetails.policyEvents.forEach(event => {
                if (!event.tranEffDate) {
                    validationErrors['tranEffDate'] = { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK };
                    BasicInfoErrorCheckForHighlight = true;
                }
            });
        }



        //   if(policyDetails.policyCoInsureds.length > 0)
        //     {
        //         if ((!policyDetails.policyCoInsureds[0].firstName || policyDetails.policyCoInsureds[0].firstName.trim().length == 0)
        //             && (!policyDetails.policyCoInsureds[0].lastName || policyDetails.policyCoInsureds[0].lastName.trim().length == 0)                       
        //         ) {
        //             validationErrors['cofirstName'] = {isValid : false, warningText :  constant.CUSTOMER_MANDATORY_DEPEDENT_FIELDS_ERROR};
        //             validationErrors['colastName'] = {isValid : false, warningText :  constant.CUSTOMER_MANDATORY_DEPEDENT_FIELDS_ERROR};  
        //             CoInsuredErrorCheckForHighlight=true;                     
        //         }

        //         if (policyDetails.policyCoInsureds[0].firstName?.trim().length > 0 && policyDetails.policyCoInsureds[0].firstName?.trim().length < 2) {
        //             validationErrors['cofirstName'] = { isValid: false, warningText: constant.CUSTOMER_FIRST_NAME_MIN_LENGTH };
        //             CoInsuredErrorCheckForHighlight=true;
        //         }

        //         if (policyDetails.policyCoInsureds[0].lastName?.trim().length > 0 && policyDetails.policyCoInsureds[0].lastName?.trim().length < 2) {
        //             validationErrors['colastName'] = { isValid: false, warningText: constant.CUSTOMER_LAST_NAME_MIN_LENGTH };
        //             CoInsuredErrorCheckForHighlight=true;
        //         }

        //         if (!policyDetails.policyCoInsureds[0].homePhone || policyDetails.policyCoInsureds[0].homePhone.trim().length == 0)
        //         {
        //             validationErrors['cohomePhone'] = {isValid : false, warningText :  constant.FIELD_CANNOT_BE_BLANK};
        //             CoInsuredErrorCheckForHighlight=true;
        //         }

        //         if (policyDetails.policyCoInsureds[0].homePhone && policyDetails.policyCoInsureds[0].homePhone?.trim().length < 10) {
        //                     validationErrors['cohomePhone'] = { isValid: false, warningText: constant.CONTACT_NO_RANGE };
        //                     CoInsuredErrorCheckForHighlight=true;
        //             }

        //          if (!policyDetails.policyCoInsureds[0].mobile || policyDetails.policyCoInsureds[0].mobile.trim().length == 0)
        //         {
        //             validationErrors['comobile'] = {isValid : false, warningText :  constant.FIELD_CANNOT_BE_BLANK};
        //             CoInsuredErrorCheckForHighlight=true;
        //         }

        //         if (policyDetails.policyCoInsureds[0].mobile && policyDetails.policyCoInsureds[0].mobile?.trim().length < 10) {
        //             validationErrors['comobile'] = { isValid: false, warningText: constant.CONTACT_NO_RANGE };
        //             CoInsuredErrorCheckForHighlight=true;
        //         }

        //         if (policyDetails.policyCoInsureds[0].workPhone && policyDetails.policyCoInsureds[0].workPhone?.trim().length < 10) {
        //             validationErrors['coworkPhone'] = { isValid: false, warningText: constant.CONTACT_NO_RANGE };
        //             CoInsuredErrorCheckForHighlight=true;
        //         }

        //         if (!policyDetails.policyCoInsureds[0].eMail || policyDetails.policyCoInsureds[0].eMail.trim().length == 0)
        //         {
        //             validationErrors['coeMail'] = {isValid : false, warningText :  constant.FIELD_CANNOT_BE_BLANK};
        //             CoInsuredErrorCheckForHighlight=true;
        //         }

        //         if (policyDetails.policyCoInsureds[0].eMail?.trim().length > 0 && !validator.isEmail(policyDetails.policyCoInsureds[0].eMail?.trim())) {
        //                 validationErrors['coeMail'] = { isValid: false, warningText: constant.CUSTOMER_INVALID_EMAIL };
        //                 CoInsuredErrorCheckForHighlight=true;
        //         }
        //  }
        //  else
        //  {
        //             validationErrors['cofirstName'] = {isValid : false, warningText :  constant.CUSTOMER_MANDATORY_DEPEDENT_FIELDS_ERROR};
        //             validationErrors['colastName'] = {isValid : false, warningText :  constant.CUSTOMER_MANDATORY_DEPEDENT_FIELDS_ERROR};                        
        //             validationErrors['cohomePhone'] = {isValid : false, warningText :  constant.FIELD_CANNOT_BE_BLANK};
        //             validationErrors['comobile'] = {isValid : false, warningText :  constant.FIELD_CANNOT_BE_BLANK};                        
        //             validationErrors['coeMail'] = {isValid : false, warningText :  constant.FIELD_CANNOT_BE_BLANK};   
        //             CoInsuredErrorCheckForHighlight=true;                     
        //  }

        //if (policyDetails.policyCoInsureds.length > 0) {
        //    const coInsured = policyDetails.policyCoInsureds[0];
        //    if (!coInsured.lastName || coInsured.lastName.trim().length === 0) {
        //        validationErrors['colastName'] = { isValid: false, warningText: constant.FIELD_BLANK };
        //        CoInsuredErrorCheckForHighlight = true;
        //    }
        //}

        if (policyDetails.policyLineOfBusinesses) {
            policyDetails.policyLineOfBusinesses.forEach(lob => {
                // Coverage and Endorsement validation: coverageCode is mandatory for each
                let coverageSectionHasError = false;
                let endorsementSectionHasError = false;
                let additionalInterestHasError = false;
                let floodUnderwritingSectionHasError = false;
                if (lob.policyCoverages && lob.policyCoverages.length > 0) {
                    lob.policyCoverages.forEach(coverage => {
                        if (!coverage.coverageCode || coverage.coverageCode.trim().length === 0) {
                            validationErrors[`coverageCode_${coverage.id}`] = { isValid: false, warningText: constant.FIELD_BLANK };
                            if (coverage.isCoverage == true) {
                                coverageSectionHasError = true;
                            } else if (coverage.isCoverage == false) {
                                endorsementSectionHasError = true;
                            }
                        }
                    });
                }
                if (coverageSectionHasError) {
                    validationErrors[constant.ValidationErrorNameList.COVERAGE] = { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK };
                }
                if (endorsementSectionHasError) {
                    validationErrors[constant.ValidationErrorNameList.ENDORSEMENT] = { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK };
                }

                // Location validation: locationNumber is mandatory for each location
                let locationSectionHasError = false;
                if (lob.policyLocations && lob.policyLocations.length > 0) {
                    lob.policyLocations.forEach(location => {
                        if (!location.locationNumber || location.locationNumber.toString().trim().length === 0) {
                            validationErrors[`locationNumber_${location.id}`] = { isValid: false, warningText: constant.FIELD_BLANK };
                            locationSectionHasError = true;
                        }

                        let zipCodeValidation = CommonUtility.validateZipCode(location.zipCode);
                        if (!zipCodeValidation.isValid) {
                            validationErrors['zipCode'] = { isValid: false, warningText: zipCodeValidation.warningText };
                            locationSectionHasError = true;
                        }
                    });
                }
                if (locationSectionHasError) {
                    validationErrors[constant.ValidationErrorNameList.LOCATION] = { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK };
                }

                lob.policyAdditionalInterests.forEach(lob => {
                    let zipCode = CommonUtility.validateZipCode(lob.zip);
                    if (!zipCode.isValid) {
                        validationErrors['zip'] = { isValid: false, warningText: zipCode.warningText };
                        additionalInterestHasError = true;
                    }

                });
                if (additionalInterestHasError) {
                    validationErrors[constant.ValidationErrorNameList.ADDITIONALINTEREST] = { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK };
                }

                // Basic Vehicle validation using existing validators from CustomerEditPolicyDetails
                let basicVehicleSectionHasError = false;
                if (lob.policyAutoVehicles && lob.policyAutoVehicles.length > 0) {
                    lob.policyAutoVehicles.forEach(vehicle => {
                        const vehicleId = vehicle.id;
                        const vin = vehicle.vin ?? vehicle.VIN;
                        const registerState = vehicle.registerState ?? vehicle.RegisterState;
                        const vehicleYear = vehicle.vehicleYear ?? vehicle.VehicleYear;
                        const make = vehicle.make ?? vehicle.Make;
                        const model = vehicle.model ?? vehicle.Model;
                        const isLeased = vehicle.isLeased ?? vehicle.IsLeased;
                        const purchaseDate = vehicle.purchaseDate ?? vehicle.PurchaseDate;
                        const leasedDate = CommonUtility.formateDate(vehicle.leasedDate ?? vehicle.LeasedDate);

                        // VIN Validation
                        const vinError = this.validateVin(vin);
                        if (vinError) {
                            validationErrors[`vin_${vehicleId}`] = { isValid: false, warningText: vinError };
                            basicVehicleSectionHasError = true;
                        }

                        // Register State Validation (mandatory)
                        const registerStateError = this.validateRegisterState(registerState);
                        if (registerStateError) {
                            validationErrors[`registerState_${vehicleId}`] = { isValid: false, warningText: registerStateError };
                            basicVehicleSectionHasError = true;
                        }

                        // Vehicle Year Validation (mandatory)
                        const yearError = this.validateVehicleYear(vehicleYear);
                        if (yearError) {
                            validationErrors[`vehicleYear_${vehicleId}`] = { isValid: false, warningText: yearError };
                            basicVehicleSectionHasError = true;
                        }

                        // Make Validation (mandatory)
                        const makeError = this.validateMake(make);
                        if (makeError) {
                            validationErrors[`make_${vehicleId}`] = { isValid: false, warningText: makeError };
                            basicVehicleSectionHasError = true;
                        }

                        // Model Validation (mandatory)
                        const modelError = this.validateModel(model);
                        if (modelError) {
                            validationErrors[`model_${vehicleId}`] = { isValid: false, warningText: modelError };
                            basicVehicleSectionHasError = true;
                        }

                        // Purchase Date is optional but must be valid MM/YY and not future when provided.
                        const purchaseDateError = this.validatePurchaseDate(purchaseDate);
                        if (purchaseDateError) {
                            validationErrors[`purchaseDate_${vehicleId}`] = { isValid: false, warningText: purchaseDateError };
                            basicVehicleSectionHasError = true;
                        }

                        // Leased Date is required when Is Leased is Yes.
                        const hasLeasedDate = leasedDate !== undefined && leasedDate !== null && String(leasedDate).trim().length > 0;
                        if (this.isLeasedYes(isLeased) && !hasLeasedDate) {
                            validationErrors[`leasedDate_${vehicleId}`] = {
                                isValid: false,
                                warningText: CustomerBasicVehicleValidation.LEASED_DATE_REQUIRED_ERROR,
                            };
                            basicVehicleSectionHasError = true;
                        } else if (hasLeasedDate) {
                            const leasedDateError = this.validateLeasedDate(leasedDate);
                            if (leasedDateError) {
                                validationErrors[`leasedDate_${vehicleId}`] = { isValid: false, warningText: leasedDateError };
                                basicVehicleSectionHasError = true;
                            }
                        }
                    });
                }

                if (basicVehicleSectionHasError) {
                    validationErrors[constant.ValidationErrorNameList.BASICVEHICLE] = { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK };
                }

                // Flood Underwriting validation (only validates first record per location, matching edit form behavior)
                if (lob.policyLocations && lob.policyLocations.length > 0) {
                    lob.policyLocations.forEach(location => {
                        // Validate first policyFlood record only (edit form only works with [0])
                        if (location.policyFloods && location.policyFloods.length > 0) {
                            const flood = location.policyFloods[0];
                            
                            // Replace Cost
                            const replaceCostValidation = this.validateReplaceCost(flood.replaceCost);
                            if (!replaceCostValidation.isValid) {
                                validationErrors[`replaceCost_${flood.id}`] = replaceCostValidation;
                                floodUnderwritingSectionHasError = true;
                            }

                            // Condo No Of Units
                            const condoUnitsValidation = this.validateCondoNoOfUnits(flood.condoNoOfUnits);
                            if (!condoUnitsValidation.isValid) {
                                validationErrors[`condoNoOfUnits_${flood.id}`] = condoUnitsValidation;
                                floodUnderwritingSectionHasError = true;
                            }

                            // Building Diagram Number
                            const bldgDiagramValidation = this.validateBldgDiagramNum(flood.bldgDiagramNum);
                            if (!bldgDiagramValidation.isValid) {
                                validationErrors[`bldgDiagramNum_${flood.id}`] = bldgDiagramValidation;
                                floodUnderwritingSectionHasError = true;
                            }

                            // Lowest Adj Grade
                            const lowestAdjValidation = this.validateLowestAdjGrade(flood.lowestAdjGrade);
                            if (!lowestAdjValidation.isValid) {
                                validationErrors[`lowestAdjGrade_${flood.id}`] = lowestAdjValidation;
                                floodUnderwritingSectionHasError = true;
                            }

                            // Lowest Floor Elev
                            const lowestFloorValidation = this.validateLowestFloorElev(flood.lowestFloorElev);
                            if (!lowestFloorValidation.isValid) {
                                validationErrors[`lowestFloorElev_${flood.id}`] = lowestFloorValidation;
                                floodUnderwritingSectionHasError = true;
                            }

                            // Base Floor Elev
                            const baseFloorValidation = this.validateBaseFloorElev(flood.baseFloorElev);
                            if (!baseFloorValidation.isValid) {
                                validationErrors[`baseFloorElev_${flood.id}`] = baseFloorValidation;
                                floodUnderwritingSectionHasError = true;
                            }
                        }

                        // Validate first policyFloodSection record only (edit form only works with [0])
                        if (location.policyFloodSections && location.policyFloodSections.length > 0) {
                            const floodSection = location.policyFloodSections[0];
                            
                            // Enclosed Floor Type
                            const enclosedFloorValidation = this.validateEnclosedFloorType(floodSection.enclosedFloorType);
                            if (!enclosedFloorValidation.isValid) {
                                validationErrors[`enclosedFloorType_${floodSection.id}`] = enclosedFloorValidation;
                                floodUnderwritingSectionHasError = true;
                            }

                            // Enclosed Size
                            const enclosedSizeValidation = this.validateEnclosedSize(floodSection.enclosedSize);
                            if (!enclosedSizeValidation.isValid) {
                                validationErrors[`enclosedSize_${floodSection.id}`] = enclosedSizeValidation;
                                floodUnderwritingSectionHasError = true;
                            }

                            // Diagram Number
                            const diagramNumberValidation = this.validateDiagramNumber(floodSection.diagramNumber);
                            if (!diagramNumberValidation.isValid) {
                                validationErrors[`diagramNumber_${floodSection.id}`] = diagramNumberValidation;
                                floodUnderwritingSectionHasError = true;
                            }
                        }
                    });
                }

                if (floodUnderwritingSectionHasError) {
                    validationErrors[constant.ValidationErrorNameList.FLOODUNDERWRITING] = { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK };
                }
            });
        }
        if (CoInsuredErrorCheckForHighlight)
            validationErrors[constant.ValidationErrorNameList.COINSURED] = { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK };

        if (BasicInfoErrorCheckForHighlight)
            validationErrors[constant.ValidationErrorNameList.BASICPOLICY] = { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK };

        if (InsuredErrorCheckForHighlight)
            validationErrors[constant.ValidationErrorNameList.INSURED] = { isValid: false, warningText: constant.FIELD_CANNOT_BE_BLANK };

        return validationErrors;
    }

    /**
     * Validates renovation year field (only when update dropdown has a value)
     */
    public validateRenovationYear = (yearValue: string, hasUpdate: boolean): { isValid: boolean; warningText: string } => {
        if (!hasUpdate) {
            return { isValid: true, warningText: "" };
        }

        if (!yearValue || !CommonUtility.isValidYear(yearValue)) {
            return { isValid: false, warningText: constant.RenovationYearValidation.VALID_YEAR_ERROR };
        }

        return { isValid: true, warningText: "" };
    };

    /**
     * Validates exterior paint year field (always validates if has value)
     */
    public validateExteriorPaintYear = (value: string): { isValid: boolean; warningText: string } => {
        if (!value) {
            return { isValid: true, warningText: "" };
        }

        if (!CommonUtility.isValidYear(value)) {
            return { isValid: false, warningText: constant.RenovationYearValidation.VALID_YEAR_ERROR };
        }

        return { isValid: true, warningText: "" };
    };

    /**
     * Comprehensive validation for all renovation year fields at save time
     */
    public validateAllRenovationYears = (policyHome: any): string[] => {
        const errors: string[] = [];

        // Check wiring
        if (CommonUtility.isRenovationSelected(policyHome.wiringUpdate)) {
            if (!policyHome.wiringYear || !CommonUtility.isValidYear(policyHome.wiringYear)) {
                errors.push(constant.RenovationYearValidation.WIRING_YEAR_ERROR);
            }
        }

        // Check plumbing
        if (CommonUtility.isRenovationSelected(policyHome.plumbingUpdate)) {
            if (!policyHome.plumbingYear || !CommonUtility.isValidYear(policyHome.plumbingYear)) {
                errors.push(constant.RenovationYearValidation.PLUMBING_YEAR_ERROR);
            }
        }

        // Check heating
        if (CommonUtility.isRenovationSelected(policyHome.heatingUpdate)) {
            if (!policyHome.heatingYear || !CommonUtility.isValidYear(policyHome.heatingYear)) {
                errors.push(constant.RenovationYearValidation.HEATING_YEAR_ERROR);
            }
        }

        // Check roofing
        if (CommonUtility.isRenovationSelected(policyHome.roofingUpdate)) {
            if (!policyHome.roofingYear || !CommonUtility.isValidYear(policyHome.roofingYear)) {
                errors.push(constant.RenovationYearValidation.ROOFING_YEAR_ERROR);
            }
        }

        // Check exterior paint
        if (policyHome.exteriorPaint && !CommonUtility.isValidYear(policyHome.exteriorPaint)) {
            errors.push(constant.RenovationYearValidation.EXTERIOR_PAINT_YEAR_ERROR);
        }

        return errors;
    };
}

export default PolicyDetailService;